import React, { useState, useRef, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';

const SearchBar = ({ onSearch, placeholder = "Search soaps, body bars, face care..." }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isExpanded, setIsExpanded] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);

  const recentSearches = [
    "Turmeric face soap",
    "Goat milk body bar",
    "Aloe vera",
    "Sea moss",
    "Intimate care"
  ];

  const popularSearches = [
    "Face Soap",
    "Body Bar",
    "Face Oil",
    "Face Care",
    "Intimate Care"
  ];

  const suggestions = [
    { term: "Activated Charcoal soap", category: "Face Care" },
    { term: "African Black soap",       category: "Body Bar" },
    { term: "Turmeric face soap",       category: "Face Care" },
    { term: "Unscented body bar",       category: "Body Bar" },
    { term: "Yoni intimate care",       category: "Intimate Care" }
  ];

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (inputRef?.current && !inputRef?.current?.contains(event?.target)) {
        setShowSuggestions(false);
        setIsExpanded(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInputChange = (e) => {
    const value = e?.target?.value;
    setSearchTerm(value);
    setShowSuggestions(value?.length > 0);
    // Live filter as you type (debounced); empty value resets to full list.
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => { onSearch((value || '').trim()); }, 250);
  };

  const handleSearch = (term = searchTerm) => {
    if (term?.trim()) {
      onSearch(term?.trim());
      setShowSuggestions(false);
      setIsExpanded(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e?.key === 'Enter') {
      handleSearch();
    }
  };

  const handleSuggestionClick = (term) => {
    setSearchTerm(term);
    handleSearch(term);
  };

  const handleFocus = () => {
    setIsExpanded(true);
    if (searchTerm?.length > 0) {
      setShowSuggestions(true);
    }
  };

  const clearSearch = () => {
    setSearchTerm('');
    setShowSuggestions(false);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    onSearch('');
    inputRef?.current?.focus();
  };

  return (
    <div className="relative w-full max-w-2xl mx-auto" ref={inputRef}>
      {/* Search Input */}
      <div className="relative">
        <div className="absolute left-3 top-1/2 transform -translate-y-1/2 z-10">
          <Icon name="Search" size={20} className="text-muted-foreground" />
        </div>
        
        <input
          type="text"
          value={searchTerm}
          onChange={handleInputChange}
          onKeyPress={handleKeyPress}
          onFocus={handleFocus}
          placeholder={placeholder}
          className="w-full pl-10 pr-20 py-3 bg-background border border-border rounded-xl text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent transition-all duration-200"
        />

        <div className="absolute right-2 top-1/2 transform -translate-y-1/2 flex items-center gap-1">
          {searchTerm && (
            <Button
              variant="ghost"
              size="icon"
              onClick={clearSearch}
              className="w-8 h-8"
            >
              <Icon name="X" size={16} />
            </Button>
          )}
          
          <Button
            variant="default"
            size="sm"
            onClick={() => handleSearch()}
            disabled={!searchTerm?.trim()}
          >
            Search
          </Button>
        </div>
      </div>
      {/* Search Suggestions Dropdown */}
      {(showSuggestions || (isExpanded && !searchTerm)) && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-background border border-border rounded-xl shadow-elevation-2 z-50 max-h-96 overflow-y-auto">
          {searchTerm ? (
            /* Search Suggestions */
            (<div className="p-4">
              <h4 className="text-sm font-medium text-foreground mb-3">Suggestions</h4>
              <div className="space-y-2">
                {suggestions?.filter(item => item?.term?.toLowerCase()?.includes(searchTerm?.toLowerCase()))?.slice(0, 5)?.map((item, index) => (
                    <button
                      key={index}
                      onClick={() => handleSuggestionClick(item?.term)}
                      className="w-full flex items-center justify-between p-2 hover:bg-muted rounded-lg text-left transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <Icon name="Search" size={16} className="text-muted-foreground" />
                        <span className="text-sm text-foreground">{item?.term}</span>
                      </div>
                      <span className="text-xs text-muted-foreground">{item?.category}</span>
                    </button>
                  ))}
              </div>
            </div>)
          ) : (
            /* Recent and Popular Searches */
            (<div className="p-4 space-y-6">
              {/* Recent Searches */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-medium text-foreground">Recent Searches</h4>
                  <Button variant="ghost" size="sm">
                    Clear
                  </Button>
                </div>
                <div className="space-y-1">
                  {recentSearches?.map((term, index) => (
                    <button
                      key={index}
                      onClick={() => handleSuggestionClick(term)}
                      className="w-full flex items-center gap-3 p-2 hover:bg-muted rounded-lg text-left transition-colors"
                    >
                      <Icon name="Clock" size={16} className="text-muted-foreground" />
                      <span className="text-sm text-foreground">{term}</span>
                    </button>
                  ))}
                </div>
              </div>
              {/* Popular Searches */}
              <div>
                <h4 className="text-sm font-medium text-foreground mb-3">Popular Searches</h4>
                <div className="flex flex-wrap gap-2">
                  {popularSearches?.map((term, index) => (
                    <button
                      key={index}
                      onClick={() => handleSuggestionClick(term)}
                      className="px-3 py-1.5 bg-muted hover:bg-muted/80 text-sm text-foreground rounded-full transition-colors"
                    >
                      {term}
                    </button>
                  ))}
                </div>
              </div>
            </div>)
          )}
        </div>
      )}
    </div>
  );
};

export default SearchBar;